import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Volume2, VolumeX } from "lucide-react";

// A looping ambient track for the homepage. Muted by default — it only begins
// once the visitor taps the speaker. From there it plays at a near-whisper and
// swells — but only to a gentle 20% — once the visitor first crosses the footer
// threshold. It never reaches full volume. While any laboratory video is
// unmuted the drone ducks.
const SRC = `${process.env.PUBLIC_URL || ""}/audio/ambient.mp3`;
const START_VOLUME = 0.06; // from the very start — a near-silent presence
const FOOTER_VOLUME = 0.2; // the footer swell — capped at 20%, never full
const DUCK_VOLUME = 0.04;
const GESTURES = ["pointerdown", "keydown", "touchstart"];

export default function AmbientDrone() {
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const audioRef = useRef(null);
  const rafRef = useRef(null);
  const rampGenRef = useRef(0);
  const unmutedVideos = useRef(new Set());
  const mutedByUser = useRef(true);
  const gestureRef = useRef(null);
  const [on, setOn] = useState(false);
  const [armed, setArmed] = useState(false);
  const [ducked, setDucked] = useState(false);
  const [atFooter, setAtFooter] = useState(false);

  useEffect(() => {
    const a = new Audio(SRC);
    a.loop = true;
    a.preload = "auto";
    a.volume = 0;
    audioRef.current = a;
    return () => {
      cancelAnimationFrame(rafRef.current);
      a.pause();
      audioRef.current = null;
    };
  }, []);

  const rampTo = (target) => {
    const a = audioRef.current;
    if (!a) return;
    cancelAnimationFrame(rafRef.current);
    const gen = ++rampGenRef.current;
    const start = a.volume;
    const t0 = performance.now();
    // A long, tidal swell when rising; a quicker settle when ducking or stopping.
    const dur = target > start ? 2600 : 900;
    const step = (t) => {
      if (gen !== rampGenRef.current) return;
      const k = Math.min(1, (t - t0) / dur);
      a.volume = Math.min(1, Math.max(0, start + (target - start) * k));
      if (k < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else if (target === 0) {
        a.pause();
      }
    };
    rafRef.current = requestAnimationFrame(step);
  };

  useEffect(() => {
    const level = ducked ? DUCK_VOLUME : atFooter ? FOOTER_VOLUME : START_VOLUME;
    rampTo(on ? level : 0);
  }, [on, ducked, atFooter]);

  const disarm = () => {
    if (gestureRef.current) {
      GESTURES.forEach((g) => window.removeEventListener(g, gestureRef.current));
      gestureRef.current = null;
    }
    setArmed(false);
  };

  const arm = () => {
    if (gestureRef.current) return;
    const onGesture = () => {
      disarm();
      enableRef.current();
    };
    gestureRef.current = onGesture;
    GESTURES.forEach((g) => window.addEventListener(g, onGesture, { once: true, passive: true }));
    setArmed(true);
  };

  const enable = async () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      await a.play();
      disarm();
      if (!on) window.dispatchEvent(new CustomEvent("prism:drone-begin"));
      setOn(true);
    } catch {
      arm();
    }
  };
  const enableRef = useRef(null);
  enableRef.current = enable;

  const mute = () => {
    mutedByUser.current = true;
    disarm();
    setOn(false);
  };

  const unmute = () => {
    mutedByUser.current = false;
    enable();
  };

  // Muted by default — playback only begins once the visitor opts in via the toggle.
  useEffect(() => {
    if (!isHome) return undefined;
    if (!mutedByUser.current) enableRef.current();
    return undefined;
  }, [isHome]);

  // The footer swell — rise to the 20% ceiling once the visitor first crosses it.
  useEffect(() => {
    if (!isHome) return undefined;
    let io;
    let raf;
    const attach = () => {
      const footer = document.querySelector('[data-testid="site-footer"]');
      if (!footer) {
        raf = requestAnimationFrame(attach);
        return;
      }
      io = new IntersectionObserver(
        ([e]) => {
          if (!e.isIntersecting) return;
          io.disconnect();
          setAtFooter(true);
          if (!mutedByUser.current) enableRef.current();
        },
        { threshold: 0.01 }
      );
      io.observe(footer);
    };
    attach();
    return () => {
      if (io) io.disconnect();
      cancelAnimationFrame(raf);
      disarm();
    };
  }, [isHome]);

  // Silence and reset when navigating away from the homepage.
  useEffect(() => {
    if (isHome) return;
    mutedByUser.current = true;
    unmutedVideos.current.clear();
    setDucked(false);
    setAtFooter(false);
    setOn(false);
    audioRef.current?.pause();
  }, [isHome]);

  // Duck while any video is producing sound.
  useEffect(() => {
    const onSound = (e) => {
      const { id, muted } = e.detail || {};
      if (!id) return;
      if (muted) unmutedVideos.current.delete(id);
      else unmutedVideos.current.add(id);
      setDucked(unmutedVideos.current.size > 0);
    };
    window.addEventListener("prism:video-sound", onSound);
    return () => window.removeEventListener("prism:video-sound", onSound);
  }, []);

  if (!isHome) return null;

  return (
    <button
      data-testid="ambient-toggle"
      data-cursor
      data-armed={armed || undefined}
      onClick={() => (on ? mute() : unmute())}
      aria-pressed={on}
      aria-label={on ? "Mute ambient sound" : "Enable ambient sound"}
      className="fixed right-4 z-40 flex h-11 w-11 items-center justify-center text-[#84DFF4]/70 transition-colors duration-500 hover:text-[#84DFF4] md:right-8 bottom-[max(1.25rem,env(safe-area-inset-bottom))]"
    >
      <span className={armed ? "ambient-armed-pulse inline-flex" : "inline-flex"}>
        {on ? <Volume2 size={15} strokeWidth={1.25} /> : <VolumeX size={15} strokeWidth={1.25} />}
      </span>
    </button>
  );
}
